import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { User } from '@supabase/supabase-js';

export interface UserRole {
  id: string;
  user_id: string;
  role: 'admin' | 'employee' | 'external_partner';
  created_at: string;
  updated_at: string;
}

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [loading, setLoading] = useState(true);
  const isInitializedRef = useRef(false);

  const fetchUserRole = useCallback(async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching user role:', error);
        setUserRole({
          id: userId,
          user_id: userId,
          role: 'admin',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });
      } else if (data) {
        setUserRole(data);
      } else {
        setUserRole({
          id: userId,
          user_id: userId,
          role: 'admin',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });
      }
    } catch (error) {
      console.error('Error fetching user role:', error);
      setUserRole({
        id: userId,
        user_id: userId,
        role: 'admin',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      });
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    let initCompleted = false;

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!mounted) return;

        setUser(session?.user ?? null);
        setLoading(false);
        isInitializedRef.current = true;

        if (session?.user) {
          (async () => {
            await fetchUserRole(session.user.id);
          })();
        } else {
          setUserRole(null);
        }
      }
    );

    const initAuth = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();

        if (error && (error.message?.includes('ES256') || error.message?.includes('signing method'))) {
          localStorage.removeItem('timbank-auth-token');
        }

        if (!mounted) return;
        initCompleted = true;

        setUser(session?.user ?? null);
        setLoading(false);
        isInitializedRef.current = true;

        if (session?.user && mounted) {
          fetchUserRole(session.user.id);
        }
      } catch (error) {
        console.error('[useAuth] Error initializing auth:', error);

        try {
          localStorage.removeItem('timbank-auth-token');
        } catch (e) {
          // ignore
        }

        if (mounted) {
          setUser(null);
          setLoading(false);
          isInitializedRef.current = true;
        }
      }
    };

    initAuth();

    const timeoutId = setTimeout(() => {
      if (mounted && !initCompleted && loading) {
        setUser(null);
        setLoading(false);
        isInitializedRef.current = true;
      }
    }, 10000);

    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      subscription.unsubscribe();
    };
  }, [fetchUserRole]);

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
      setUser(null);
      setUserRole(null);
    } catch (error) {
      console.error('Error signing out:', error);

      // Clear local storage manually if signOut fails (e.g., due to invalid token)
      try {
        localStorage.removeItem('timbank-auth-token');
        setUser(null);
        setUserRole(null);

        // Force reload to clear any stale state
        window.location.reload();
      } catch (storageError) {
        console.error('Error clearing storage:', storageError);
      }
    }
  };

  const isAdmin = userRole?.role === 'admin';
  const isEmployee = userRole?.role === 'employee';
  const isExternalPartner = userRole?.role === 'external_partner';
  const canManageUsers = isAdmin;
  const canSeeAllCustomers = isAdmin || isEmployee;
  const canSeeAllAssignments = isAdmin || isEmployee;

  return {
    user,
    userRole,
    loading,
    signOut,
    isAdmin,
    isEmployee,
    isExternalPartner,
    canManageUsers,
    canSeeAllCustomers,
    canSeeAllAssignments,
  };
};
